<template>
  <div class="clubs-map">
    <slot name="top"></slot>
    <div class="clubs-map__container" :class="{ 'd-none': !showMap }">
      <div class="clubs-map__top">
        <div
          class="clubs-map__region-select clubs-map__top-item"
          v-show="!hideStats"
        >
          <Select
            class="multiselect_wrap"
            v-model="region.value"
            :value="region.value"
            :options="region.options"
            :loading="!regionsCount"
            searchable
            @input="() => searchRegion(false)"
          >
          </Select>
        </div>
        <div class="clubs-map__activity clubs-map__top-item">
          <label class="checkbox">
            <span class="checkbox__content">
              <input
                v-model="showActivity"
                class="checkbox__input"
                type="checkbox"
                name="show_activity"
                @change="toggleActivity"
              >
              <span class="checkbox__marker">
                <icon icon-name='check-bold'></icon>
              </span>
              <span class="checkbox__text">Активность</span>
            </span>
          </label>
        </div>
      </div>
      <div class="clubs-map__map">
        <div id="map" style="width: 100%; height: 100%;"></div>
        <div class="clubs-map__legend" v-show="showActivity">
          <div class="clubs-map__legend-title">
            Активность клубов
          </div>
          <div class="clubs-map__legend-scale"></div>
        </div>
      </div>
      <div class="clubs-map__stats" v-show="!hideStats">
        <div class="clubs-map__stat">
          <div class="clubs-map__stat-value">
            <span class="h3-m">
              {{ regionClubsCount === null ? '--' : regionClubsCount }}
            </span>
            <span class="color-muted">
              / {{ totalClubsCount === null ? '--' : totalClubsCount }}
            </span>
          </div>
          <div class="clubs-map__stat-title">Клубы&nbsp;
            <v-popover placement="bottom-center" trigger="hover">
              <span class="link link_light">
                <icon icon-name='info-circle' class-name="icon_fix" w="18" h="18"></icon>
              </span>
              <template slot="popover">
                <template v-if="this.regionSelected">Клубы в&nbsp;регионе /<br>Всего в&nbsp;России</template>
                <template v-else>Клубы в&nbsp;России /<br>Всего на&nbsp;RussiaRunning</template>
              </template>
            </v-popover>
          </div>
        </div>
        <div class="clubs-map__stat">
          <div class="clubs-map__stat-value">
            <span class="h3-m">
              {{ regionMembersCount === null ? '--' : regionMembersCount }}
            </span>
            <span class="color-muted">
              / {{ totalMembersCount === null ? '--' : totalMembersCount }}
            </span>
          </div>
          <div class="clubs-map__stat-title">Участники&nbsp;
            <v-popover placement="bottom-center" trigger="hover">
              <span class="link link_light">
                <icon icon-name='info-circle' class-name="icon_fix" w="18" h="18"></icon>
              </span>
              <template slot="popover">
                <template v-if="this.regionSelected">Участники в&nbsp;регионе /<br>Всего в&nbsp;России</template>
                <template v-else>Участники в&nbsp;России /<br>Всего на&nbsp;RussiaRunning</template>
              </template>
            </v-popover>
          </div>
        </div>
      </div>
      <div
        class="clubs-map__overlay overlay overlay_light overlay_flex"
        v-show="loading"
      >
        <div class="overlay__content">
          <div class="loader"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import champsService from '@/services/champsService'
import Select from '@/components/Select'
import { mapState } from 'vuex'

function declOfNum (number, titles) {
  const cases = [2, 0, 1, 1, 1, 2]
  return titles[(number % 100 > 4 && number % 100 < 20) ? 2 : cases[(number % 10 < 5) ? number % 10 : 5]]
}

export default {
  name: 'ClubsMap',
  components: {
    Select
  },
  props: {
    searchString: String,
    sportTypes: Array,
    city: String,
    showMap: Boolean
  },
  data () {
    return {
      map: null,
      loading: true,
      region: {
        value: 'default',
        options: [
          { id: 'default', name: 'Вся Россия', nameInSelection: 'Россия' }
        ],
        clubsCount: null,
        membersCount: null
      },
      regionsCount: null,
      points: [],
      statistics: {
        ruTotalCount: null,
        ruTotalMembers: null,
        totalCount: null,
        totalMembers: null
      },
      cachedRegions: {},
      hideStats: false,
      showActivity: false
    }
  },
  computed: {
    ...mapState('clubs', ['clubsCount']),
    regionSelected () {
      return this.region.value !== 'default'
    },
    regionClubsCount () {
      return this.regionSelected
        ? this.region.clubsCount
        : this.statistics.ruTotalCount
    },
    regionMembersCount () {
      return this.regionSelected
        ? this.region.membersCount
        : this.statistics.ruTotalMembers
    },
    totalClubsCount () {
      return this.regionSelected
        ? this.statistics.ruTotalCount
        : this.statistics.totalCount
    },
    totalMembersCount () {
      return this.regionSelected
        ? this.statistics.ruTotalMembers
        : this.statistics.totalMembers
    }
  },
  methods: {
    toggleActivity () {
      this.map.regionsOpacity = +!this.showActivity
      this.map.toggleClustererVisibility(!this.showActivity)

      if (this.showActivity) {
        this.map.unmarkRegion()
        this.map.showHeatmap()
        return
      }

      this.map.hideHeatmap()
      if (!this.hideStats) {
        this.map.markRegion(this.region.value === 'default' ? null : this.region.value)
      }
    },
    getPoints () {
      this.loading = true
      return champsService.getClubMap({
        searchString: this.searchString,
        sportTypes: this.sportTypes,
        city: this.city
      })
        .then((response) => {
          const info = response.data || {}

          this.statistics = {
            ruTotalCount: info.RuTotalCount || 0,
            ruTotalMembers: info.RuTotalMembers || 0,
            totalCount: info.TotalCount || 0,
            totalMembers: info.TotalMembers || 0
          }

          let points = info.Items || []
          if (points.length) {
            points = points.map((item) => ({
              id: item.Id,
              latitude: item.Latitude,
              longitude: item.Longitude,
              title: item.Name,
              url: item.Url,
              membersCount: item.MembersCount,
              activePercent: item.ActivePercents
            }))
            // Sort points by activePercent (desc)
            points = points.sort((a, b) => b.activePercent - a.activePercent)
          }
          this.points = points
          this.map.setPoints(points)
          if (this.showActivity) {
            this.map.showHeatmap()
          }
          if (!this.hideStats) {
            this.setRegionsWithClubs()
          }
        })
        .catch(() => {
          this.map.setPoints([])
          this.points = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    update () {
      this.hideStats = this.$parent.hasFilters
      return this.getPoints()
    },
    async create () {
      this.map = new window.InteractiveMap('map')
      await this.map.create()
      this.map.getRegions()
      await this.update()

      if (!this.hideStats) {
        this.searchRegion(true)
      }
    },
    async searchRegion (preventZoom) {
      const region = this.region.value
      this.region.clubsCount = null
      this.region.membersCount = null
      if (region === 'default') {
        await this.map.markRegion()
      } else {
        await this.map.markRegion(region)
        this.map.getPlacemarksInsideBorders()
          .then((res) => {
            const ids = res.ids
            this.region.clubsCount = ids.length
            this.getClubsMembersCount(region, ids)
          })
      }
      if (!preventZoom) {
        this.map.zoomToBorders()
      }
    },
    async getClubsMembersCount (iso, ids) {
      if (this.cachedRegions[iso]) {
        this.region.membersCount = this.cachedRegions[iso].Count
        return
      }
      const response = await champsService.getClubsMembersCount(ids)
      if (response.status === 200) {
        this.cachedRegions[iso] = response.data
        this.region.membersCount = response.data.Count
      }
    },
    addMapScript () {
      if (window.InteractiveMap) {
        this.create()
        return
      }
      const script = document.createElement('script')
      script.async = true
      script.onload = this.create
      script.src = process.env.BASE_URL + 'files/interactive-map.js'
      document.body.appendChild(script)
    },
    async setRegionsWithClubs () {
      if (typeof this.regionsCount !== 'number') {
        const options = await this.map.getRegionsWithPlacemarks()
        this.regionsCount = options.length
        this.region.options[0].nameInSelection = `Россия (${this.regionsCount} ${declOfNum(this.regionsCount, ['регион', 'региона', 'регионов'])} с клубами)`
        this.region.options.push(...options)
      }
    }
  },
  mounted () {
    this.addMapScript()
  },
  beforeDestroy () {
    this.map.destroy()
  },
  watch: {
    hideStats (val) {
      if (val) {
        this.region.value = 'default'
        this.map.unmarkRegion()
      } else {
        this.map.markRegion()
      }
    },
    showMap (val) {
      if (val && this.map) {
        this.map.fixZoom()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.clubs-map {
  background-color: map-get($color-neutral-black, 4);
  border-radius: 4px;
  padding: 16px;
  position: relative;

  @media (min-width: breakpoint(md)) {
    padding: 8px;
  }

  &__container {
    width: 100%;
    position: relative;
  }

  &__map {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 344px;
    background-color: $color-white;
  }

  &__overlay {
    z-index: 1;
  }

  &__top {
    display: flex;
    flex-direction: column;
    z-index: 1;
    position: relative;
    margin-right: -8px;

    @media (min-width: breakpoint(md)) {
      flex-direction: row;
      position: absolute;
      align-items: center;
      top: 8px;
      left: 8px;
    }
  }

  &__top-item {
    display: flex;
    align-items: center;
    background-color: $color-white;
    box-shadow: $elevation-deth-4;
    height: 40px;
    border-radius: 2px;
    margin-right: 8px;
    padding: 0 12px;

    @media (min-width: breakpoint(lg)) {
      height: 32px;
    }

    ::v-deep {

      .multiselect {
        border: 0;
        box-shadow: none;
        height: 100%;
        min-width: 100%;
      }

      .multiselect-container {
        min-width: 100%;
      }
    }

    ::v-deep .multiselect__input,
    ::v-deep .multiselect__single,
    .checkbox__text {
      font-size: 14px;
    }
  }

  &__activity {

    @media (max-width: breakpoint(md, max)) {
      position: absolute;
      top: 100%;
      margin: 8px;
    }
  }

  &__region-select {
    padding: 0;
    margin-bottom: 8px;

    @media (min-width: breakpoint(md)) {
      width: 300px;
      margin-bottom: 0;
    }
  }

  &__stats {
    margin-top: 1rem;
    margin-bottom: -8px;
    pointer-events: none;

    @media (min-width: breakpoint(md)) {
      position: absolute;
      display: flex;
      flex-direction: column;
      justify-content: center;
      top: 0;
      right: 0;
      height: 100%;
      margin: 0;
      padding: 8px;
    }
  }

  &__stat {
    flex: 0 0 auto;
    font-weight: 600;
    border-radius: 4px;
    border: 1px solid map-get($color-neutral-black, 4);
    background-color: $color-white;
    box-shadow: $elevation-deth-4;
    overflow: hidden;
    padding: 18px 16px;
    position: relative;
    text-align: center;
    margin-bottom: 8px;
    min-width: 160px;
    pointer-events: auto;
  }

  &__stat-title {
    @include text-xs;
    margin-top: 0.5rem;
  }

  &__stat-value {
    @include text-sm;
    white-space: nowrap;
  }

  &__legend {
    position: absolute;
    bottom: 0;
    left: 0;
    margin: 8px;

    @media (max-width: breakpoint(sm, max)) {

      .map-balloon-opened ~ & {
        z-index: -1;
      }
    }
  }

  &__legend-title, &__legend-scale {
    box-shadow: $elevation-deth-4;
    border-radius: 99px;
    font-weight: $font-weight-semibold;
    padding: 0 8px;
  }

  &__legend-title {
    display: inline-block;
    vertical-align: bottom;
    @include text-xs;
    background-color: $color-white;
    margin-bottom: 4px;
  }

  &__legend-scale {
    @include text-xs;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $color-white;
    background: linear-gradient(90deg, #30CE12 0%, #9ADA1B 13.54%, #FFE424 27.6%, #ECC918 42.19%, #FBAE1E 56.77%, #F87C19 70.83%, #EE4613 85.42%, #620801 100%);
    border: 1px solid map-get($color-neutral-white, 85);
    width: 140px;
    height: 18px;

    @media (min-width: breakpoint(md)) {
      width: 194px;
    }
  }

  &__legend-tooltip {
    display: inline-block;
    vertical-align: middle;
    width: 18px;
    height: 18px;
    background-color: $color-white;
    box-shadow: $elevation-deth-4;
    border-radius: 99px;

    svg {
      display: block;
      color: map-get($color-neutral-black, 45);
    }
  }
}
</style>
